import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import type { FlightSegmentSource } from '../results/flight_offer_source.js';
import type { TariffFetchState } from './fare_expansion.js';
import type { FlightFaresCopy } from './fares_copy.js';

/**
 * O que os DOIS desenhos da tela "Escolha as tarifas" compartilham — o largo
 * (`fares_page.tsx`) e o do celular (`fares_page_stack.tsx`, #3875): os tipos
 * das props e a faixa de espera/falha. Fora dos dois arquivos para nenhum
 * importar o outro.
 */

/** O voo escolhido de UMA perna: o ref opaco (para os drawers de detalhe e mapa
 *  de assentos) e o trecho a desenhar. */
export interface FaresPageFlight {
  offerId: string;
  seg: FlightSegmentSource;
  stops: number;
}

/** Sentido do trecho — o índice da perna já resolvido em vocabulário. */
export type FaresLegKind = 'outbound' | 'return';

/** A porta do drawer de detalhe (ou do mapa de assentos, `focus: 'seats'`) de
 *  um voo escolhido. O drawer é do host: ele monta onde a tela dele vive. */
export type OpenFlightDetail = (
  flight: FaresPageFlight,
  kind: FaresLegKind,
  focus?: 'seats',
) => void;

/** Faixa de espera/erro no lugar dos trilhos — a página em si (cabeçalho e voos
 *  escolhidos) fica de pé: o que falta é a lista de famílias, não o contexto. */
export function FaresStatus({
  state,
  onRetry,
  onResearch,
  onChangeFlight,
  readOnly,
  copy,
}: Readonly<{
  /** O estado INTEIRO (e não a fase): `ready` não chega aqui por construção, mas
   *  é o tipo que garante isso — não um comentário. */
  state: TariffFetchState;
  onRetry: () => void;
  onResearch: (() => void) | undefined;
  /** O "Alterar voo" da perna sem tarifa (#6000) — fora da tela somente leitura. */
  onChangeFlight: (leg: 0 | 1) => void;
  readOnly: boolean | undefined;
  copy: FlightFaresCopy;
}>) {
  if (state.phase === 'ready') return null;
  if (state.phase === 'loading') {
    return (
      // `VOLL/Skeleton` é o que o frame instancia aqui, então a pulsação e a
      // areia saem do átomo (`animate-pulse bg-muted`) e não de um `@keyframes`
      // desta folha. O que continua sendo da TELA é a silhueta — 180x176 e o
      // raio de 14px são o card de família que vai ocupar o lugar — e a escada
      // de atraso, que faz o trilho chegar da esquerda pra direita.
      <output className="ffs-skel">
        <Skeleton className="ffs-skel-card rounded-[14px]" />
        <Skeleton className="ffs-skel-card rounded-[14px] [animation-delay:90ms]" />
        <Skeleton className="ffs-skel-card rounded-[14px] [animation-delay:180ms]" />
      </output>
    );
  }
  if (state.phase === 'leg_unavailable') {
    // A companhia tarifou só a outra perna (#6000): o "Buscar de novo" devolveria
    // o mesmo voo sem tarifa — o loop que esta faixa corta. A ação é trocar o voo.
    const { leg } = state;
    return (
      <div className="ffs-status" role="alert">
        <span>{copy.faresLegUnavailable(leg)}</span>
        {!readOnly && (
          <button
            type="button"
            className="ffs-retry"
            onClick={() => {
              onChangeFlight(leg === 'outbound' ? 0 : 1);
            }}
          >
            {copy.changeFlight}
          </button>
        )}
      </div>
    );
  }
  if (state.phase === 'stale') {
    return (
      <div className="ffs-status" role="alert">
        <span>{copy.faresStale}</span>
        {onResearch && (
          <button type="button" className="ffs-retry" onClick={onResearch}>
            {copy.searchAgain}
          </button>
        )}
      </div>
    );
  }
  return (
    <div className="ffs-status" role="alert">
      <span>{copy.faresError}</span>
      <button type="button" className="ffs-retry" onClick={onRetry}>
        {copy.tryAgain}
      </button>
    </div>
  );
}
